import { useState, useRef, useCallback } from 'react';

/*
 * Copyright 2021 Subito.it SRL. All rights reserved.
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 *
 * This file has been edited by Subito.it SRL
 */
function useControlledState(value, defaultValue, onChange) {
    const [stateValue, setStateValue] = useState(value || defaultValue);
    const ref = useRef(value !== undefined);
    const wasControlled = ref.current;
    const isControlled = value !== undefined;
    const stateRef = useRef(stateValue);
    if (wasControlled !== isControlled) {
        console.warn(`WARN: A component changed from ${wasControlled ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}.`);
    }
    ref.current = isControlled;
    const setValue = useCallback((value) => {
        const onChangeCaller = (value) => {
            if (onChange) {
                if (!Object.is(stateRef.current, value)) {
                    onChange(value);
                }
            }
            if (!isControlled) {
                stateRef.current = value;
            }
        };
        if (value instanceof Function) {
            const updateFunction = (oldValue) => {
                const interceptedValue = value(isControlled ? stateRef.current : oldValue);
                onChangeCaller(interceptedValue);
                if (!isControlled) {
                    return interceptedValue;
                }
                return oldValue;
            };
            setStateValue(updateFunction);
        }
        else {
            if (!isControlled) {
                setStateValue(value);
            }
            onChangeCaller(value);
        }
    }, [isControlled, onChange]);
    // If a controlled component's value prop changes, we need to update stateRef
    if (isControlled) {
        stateRef.current = value;
    }
    else {
        value = stateValue;
    }
    return [value, setValue];
}

export { useControlledState as default };
//# sourceMappingURL=useControlledState.js.map
